/**
 * Schedulers that run on the window the user can see (RC2 QA V70).
 *
 * While a detached viewer is in front, the main window is hidden and the browser
 * throttles its `requestAnimationFrame` (to nothing) and its timers (to ~1 Hz).
 * Preact's hooks flush effects through the main window's rAF + `setTimeout`, and
 * the EEG sources debounce viewport changes with the main window's `setTimeout`,
 * so a page step in a detached sEEG window reached its traces seconds after the
 * SAM. These helpers schedule on `visibleWindow()` instead. When the main window
 * is the visible one they use the very same global functions as before, so
 * nothing changes without a detached window in front.
 */

import { options } from 'preact';
import { defer, Observable, timer } from 'rxjs';

import { visibleWindow } from '@brainfocus/web/shared/platform/popout_registry';

/** Preact's own fallback when no frame comes (`hooks/src/index.js`, RAF_TIMEOUT). */
const RAF_TIMEOUT_MS = 35;

interface TimerHost {
    setTimeout(handler: () => void, timeout?: number): number;
    clearTimeout(id: number): void;
    requestAnimationFrame?(callback: () => void): number;
    cancelAnimationFrame?(id: number): void;
}

/** The visible window's timers; the global ones when that is the main window. */
function visibleTimerHost(): TimerHost {
    const win = visibleWindow();
    return (win === window ? globalThis : win) as unknown as TimerHost;
}

/**
 * Preact's `afterNextFrame`, on the visible window: run `callback` after that
 * window's next frame, with a timeout fallback in case no frame comes.
 *
 * On a detached window, a main-window timeout backs it up and the callback runs
 * once, whichever fires first: closing the detached window drops its timers, and
 * Preact, which only schedules a flush when its queue goes from empty to one,
 * would then never run an effect again (RC2 re-review).
 */
export function visibleAfterNextFrame(callback: () => void): void {
    const host = visibleTimerHost();
    const onMain = host === (globalThis as unknown as TimerHost);
    const hasRaf = typeof host.requestAnimationFrame === 'function';
    let raf: number | undefined;
    let ran = false;
    let backup: ReturnType<typeof setTimeout> | undefined;
    const run = (): void => {
        if (ran) return;
        ran = true;
        if (backup !== undefined) clearTimeout(backup);
        callback();
    };
    const done = (): void => {
        host.clearTimeout(timeout);
        if (hasRaf && raf !== undefined) host.cancelAnimationFrame?.(raf);
        host.setTimeout(onMain ? callback : run);
    };
    const timeout = host.setTimeout(done, RAF_TIMEOUT_MS);
    if (hasRaf) raf = host.requestAnimationFrame!(done);
    if (!onMain) backup = setTimeout(run, RAF_TIMEOUT_MS);
}

/** Make Preact's hooks flush their effects on the visible window. Call once at app start. */
export function installVisibleWindowHooksScheduler(): void {
    options.requestAnimationFrame = visibleAfterNextFrame;
}

/**
 * A one-shot timer emitting `0` after `ms` and completing, created on the window
 * visible at subscription time: rxjs `timer(ms)` itself when that is the main
 * window, else a timeout of the detached window.
 */
export function visibleTimer(ms: number): Observable<0> {
    return defer(() => {
        const win = visibleWindow();
        if (win === window) return timer(ms) as Observable<0>;
        return new Observable<0>((subscriber) => {
            const id = win.setTimeout(() => {
                subscriber.next(0);
                subscriber.complete();
            }, ms);
            return () => win.clearTimeout(id);
        });
    });
}
